import React from "react";
import { LateTag, OrigenChip, StatusChip } from "./Chips";
import {
  CANAL_LABELS,
  EMPTY,
  MOTIVO_CANCELACION_LABELS,
  codeOf,
  demoraTitle,
  formatCuit,
  formatDate,
  formatDuration,
  formatRelativeDays,
  formatRollos,
  formatShortDate,
  labelFor,
  rollosWord,
  toIsoDate,
} from "../rolloFormat";

// Cell contents shared by the tables and the mobile cards. Each cell has a main line and, below it, a caption
// in ink-muted (design DataTable). Missing data is written with words or a dash.

export const isMine = (item, currentUserId) =>
  currentUserId !== undefined &&
  currentUserId !== null &&
  item?.responsableUsuarioId !== undefined &&
  item?.responsableUsuarioId !== null &&
  Number(item.responsableUsuarioId) === Number(currentUserId);

export const isCancelada = (item) => codeOf(item?.estado) === "CANCELADO";

// Row pointed at ("Ver gestión abierta") or just created; a new one also gets the "Nueva" tag.
export const isSpotlight = (item, spotlight) => Boolean(spotlight) && item.id === spotlight.id;
export const newBadge = (item, spotlight) => (isSpotlight(item, spotlight) && spotlight.isNew ? "Nueva" : null);

const terminalLabel = (item) => (item?.numeroTerminal ? `Terminal ${item.numeroTerminal}` : "Sin terminal");
const provinciaLabel = (item) => item?.provincia || "Sin provincia";

export const Muted = ({ children = EMPTY }) => <span className="crm-rollo__muted">{children}</span>;

// Tag next to the name of a gestión just created ("Nueva", design RolloNewSelect).
const NewTag = ({ children }) => (children ? <span className="crm-rollo__new-tag">{children}</span> : null);

export function ComercioCell({ item, compact = false, badge = null }) {
  if (compact) {
    return (
      <>
        <span className="crm-rollo__t">{item.nombreComercio || "Comercio sin nombre"}</span>
        <NewTag>{badge}</NewTag>
        <span className="crm-rollo__s">{`CUIT ${formatCuit(item.cuit)} · ${terminalLabel(item)}`}</span>
        <span className="crm-rollo__s crm-rollo__s--tight">{provinciaLabel(item)}</span>
      </>
    );
  }

  return (
    <>
      <span className="crm-rollo__t">{item.nombreComercio || "Comercio sin nombre"}</span>
      <NewTag>{badge}</NewTag>
      <span className="crm-rollo__s">{`CUIT ${formatCuit(item.cuit)}`}</span>
      <span className="crm-rollo__s crm-rollo__s--tight">{`${terminalLabel(item)} · ${provinciaLabel(item)}`}</span>
    </>
  );
}

export function EstadoCell({ item }) {
  return (
    <>
      <StatusChip estado={item.estado} />
      <div className="crm-rollo__s-row">
        <span>{`vía ${labelFor(CANAL_LABELS, item.canalPedido)}`}</span>
        <OrigenChip origen={item.origen} />
      </div>
    </>
  );
}

export function CantidadCell({ item }) {
  const cantidad = Number(item.cantidadSolicitada) || 0;
  return (
    <>
      <span className="crm-rollo__t">{cantidad}</span>
      <span className="crm-rollo__s">{rollosWord(cantidad)}</span>
    </>
  );
}

// The server decides demorada; diasParaDemora (list response) only words the tag title.
export function AntiguedadCell({ item, diasParaDemora = null }) {
  const relative = formatRelativeDays(item.diasAbierta);
  return (
    <>
      {formatDate(item.createdAt)}
      <div className="crm-rollo__age">
        {item.demorada ? (
          <LateTag title={demoraTitle(diasParaDemora)}>{`Demorada · ${relative}`}</LateTag>
        ) : (
          <span className="crm-rollo__s crm-rollo__s--flat">{relative}</span>
        )}
      </div>
    </>
  );
}

export function UltimaEntregaCell({ item }) {
  const ultima = item.ultimaEntrega;
  if (!ultima) return <Muted>Sin entregas previas</Muted>;

  return (
    <>
      {formatDate(ultima.fecha)}
      <span className="crm-rollo__s">{formatRollos(ultima.cantidad)}</span>
    </>
  );
}

// Activas: the cadete only shows while the gestión is En camino ("Salió dd/mm").
export function CadeteCell({ item }) {
  if (codeOf(item.estado) !== "EN_CAMINO" || !item.cadete) return <Muted />;

  return (
    <>
      {item.cadete}
      {item.fechaSalida ? <span className="crm-rollo__s">{`Salió ${formatShortDate(item.fechaSalida)}`}</span> : null}
    </>
  );
}

export function ResponsableCell({ item, currentUserId, compact = false }) {
  if (!item.responsableUsuarioId && !item.responsableNombre) return <Muted>Sin asignar</Muted>;

  const nombre = item.responsableNombre || `Usuario ${item.responsableUsuarioId}`;
  if (!isMine(item, currentUserId)) return <>{nombre}</>;

  return compact ? (
    <>
      {nombre} <Muted>(vos)</Muted>
    </>
  ) : (
    <>
      {nombre}
      <span className="crm-rollo__s">Vos</span>
    </>
  );
}

export function ProximaAccionCell({ item, today }) {
  if (!item.proximaAccion) return <Muted />;

  const fecha = item.fechaProximaAccion;
  return (
    <>
      {item.proximaAccion}
      {fecha ? (
        <span className="crm-rollo__s">{`${formatDate(fecha)}${today && toIsoDate(fecha) === today ? " · hoy" : ""}`}</span>
      ) : null}
    </>
  );
}

// Finalizadas: requested / delivered. A different delivered quantity goes in the amber tag.
export function PedidaEntregadaCell({ item }) {
  const pedida = Number(item.cantidadSolicitada) || 0;
  if (isCancelada(item)) {
    return (
      <>
        <span className="crm-rollo__t">{pedida}</span> <Muted>/ —</Muted>
        <span className="crm-rollo__s">no se entregó</span>
      </>
    );
  }

  const entregada = item.cantidadEntregada;
  const hasEntregada = entregada !== null && entregada !== undefined;
  return (
    <>
      <span className="crm-rollo__t">{pedida}</span> <Muted>/</Muted>{" "}
      {!hasEntregada ? (
        <Muted />
      ) : Number(entregada) !== pedida ? (
        <LateTag title="Difiere de lo pedido">{entregada}</LateTag>
      ) : (
        <span className="crm-rollo__t">{entregada}</span>
      )}
      <span className="crm-rollo__s">rollos</span>
    </>
  );
}

export function FechasCell({ item }) {
  return (
    <>
      <Muted>Pedido</Muted> {formatDate(item.createdAt)}
      <span className="crm-rollo__s crm-rollo__s--ink">
        <Muted>Cierre</Muted> {formatDate(item.fechaCierre)}
      </span>
    </>
  );
}

export const DuracionCell = ({ item }) => <>{formatDuration(item.diasAbierta)}</>;

export const FinCadeteCell = ({ item }) => (item.cadete ? <>{item.cadete}</> : <Muted />);

export const motivoLabel = (item) => labelFor(MOTIVO_CANCELACION_LABELS, item.motivoCancelacion);

export function RecibioCell({ item }) {
  if (isCancelada(item)) {
    return (
      <>
        <Muted>Motivo</Muted>
        <span className="crm-rollo__s crm-rollo__s--ink">{motivoLabel(item)}</span>
      </>
    );
  }

  return item.recibioNombre ? <>{item.recibioNombre}</> : <Muted />;
}
